import { coerceBooleanProperty } from "@angular/cdk/coercion";
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Host,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  ViewEncapsulation,
  forwardRef
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subscription } from "rxjs";

import { CheckboxGroupComponent } from "./checkbox-group.component";

@Component({
  selector: "ta-checkbox",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true
    }
  ],
  template: `
    <span [taTooltipStatic]="disabled ? disabledTooltipMessage : null"
      ><ng-content></ng-content
    ></span>
  `,
  encapsulation: ViewEncapsulation.None
})
export class CheckboxComponent
  implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor {
  @HostBinding("class.checkbox")
  get hostClass(): boolean {
    return true;
  }

  @HostBinding("attr.tabindex")
  get tabIndex(): number {
    return this.isDisabled ? -1 : 0;
  }

  @Input()
  @HostBinding("class.checkbox--checked")
  get checked(): boolean {
    return this._checked;
  }
  set checked(value: boolean) {
    value = value != null && value !== false;
    if (this._checked === value) {
      return;
    }
    this._checked = value;
    this.checkedChange.emit(value);
  }
  protected _checked: boolean = false;

  @Output() checkedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input()
  @HostBinding("class.checkbox--indeterminate")
  get indeterminate(): boolean {
    return this._indeterminate;
  }
  set indeterminate(value: boolean) {
    value = value != null && value !== false;
    if (this._indeterminate === value) {
      return;
    }

    this._indeterminate = value;
    this.indeterminateChange.emit(value);

    if (value) {
      this.checked = false;
    }
  }
  private _indeterminate: boolean = false;

  @Output() indeterminateChange: EventEmitter<boolean> = new EventEmitter<
    boolean
  >();

  @Input()
  // TODO: make this settable once
  // TODO: should this be generic?
  value?: any;

  @Input()
  // TODO: make this settable once
  get toggleGroups(): string | string[] {
    return this._toggleGroups;
  }
  set toggleGroups(groups: string | string[]) {
    if (typeof groups === "string") {
      groups = (groups as string).split(",");
    }
    this.toggleGroupsSet = new Set(groups);
    if (groups.length !== this.toggleGroupsSet.size) {
      groups = Array.from(this.toggleGroupsSet);
    }
    this._toggleGroups = groups;
  }
  private _toggleGroups: string[] = [];
  private toggleGroupsSet: Set<string> = new Set();

  @HostBinding("class.checkbox--toggle-member")
  get isToggleMember(): boolean {
    return this._toggleGroups.length > 0;
  }

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    value = value != null && value !== false;
    if (this._disabled === value) {
      return;
    }
    this._disabled = value;
  }
  private _disabled: boolean = false;

  @Input()
  disabledTooltipMessage?: string;

  @HostBinding("class.checkbox--disabled")
  get isDisabled(): boolean {
    return this.disabled || (this.parent != null && this.parent.disabled);
  }

  @HostBinding("class.checkbox--touched")
  get touched(): boolean {
    return this._touched;
  }
  set touched(value: boolean) {
    value = value != null && value !== false;
    if (this._touched === value) {
      return;
    }
    this._touched = value;
    this.touchedChange.emit(value);
  }
  private _touched: boolean = false;

  // TODO: do we need this as an event? on touch? should not be touchedChange since we're not 2-way
  @Output() touchedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @HostListener("click", ["$event"])
  @HostListener("keydown.space", ["$event"])
  onInteraction(event: Event): void {
    if (this.isDisabled) {
      return;
    }

    event.preventDefault();

    this.touched = true;
    this.toggle();
  }

  @Input()
  @HostBinding("class.checkbox--no-top-margin")
  get noTopMargin(): boolean {
    return this._noTopMargin;
  }
  set noTopMargin(value: boolean) {
    this._noTopMargin = coerceBooleanProperty(value);
  }
  private _noTopMargin = false;

  constructor(
    @Host() @Optional() private parent: CheckboxGroupComponent<any>,
    public elementRef: ElementRef
  ) {
    this.initControlAccessors();
  }

  ngOnInit(): void {
    if (this.parent != null) {
      this.parent.addCheckbox(this);
    }
  }

  private parentValueChangeSubscription?: Subscription;
  ngAfterViewInit(): void {
    if (this.parent != null) {
      this.parentValueChangeSubscription = this.parent.valueChange.subscribe(
        () => {
          this.checked = this.parent.isSelected(this.value);
        }
      );
    }
  }

  ngOnDestroy(): void {
    if (this.parent != null) {
      if (this.parentValueChangeSubscription != null) {
        this.parentValueChangeSubscription.unsubscribe();
      }
      Promise.resolve().then(() => {
        this.parent.removeCheckbox(this);
      });
    }
  }

  toggle(): void {
    if (this.indeterminate) {
      this.checked = false;
    } else {
      this.checked = !this.checked;
    }
    this.indeterminate = false;
  }

  isInToggleGroup(group: string): boolean {
    return this.toggleGroupsSet.has(group);
  }

  initControlAccessors(): void {
    this.checkedChange.subscribe((value) => {
      if (this._onChangeFn == null) {
        return;
      }
      this._onChangeFn(value);
    });

    let touchSubscription: {
      unsubscribe: () => void;
    } = this.touchedChange.subscribe(() => {
      if (this._onTouchedFn != null) {
        this._onTouchedFn();
      }
      touchSubscription.unsubscribe();
    });
  }

  writeValue(value: any): void {
    this.checked = value;
  }

  private _onChangeFn?: (value: boolean) => void;
  registerOnChange(fn: any): void {
    this._onChangeFn = fn;
  }

  private _onTouchedFn?: () => void;
  registerOnTouched(fn: any): void {
    this._onTouchedFn = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }
}
